import React, { useCallback, useMemo } from "react";

import { Formik, Form } from "formik";
import * as yup from "yup";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { postService } from "../../utils/ServiceUtils";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { InputForPhone } from "./InputForPhone";

export const StyledLabel = styled(Typography)(() => ({
  marginTop: "4px",
  marginBottom: "2px",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "#606567",
}));
const RequiredAsterisk = () => (
  <span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
);
const BODY_TEXT_HTML = {
    __html: `Enter the participant's name and at least one contact method to send a meeting invite.`,
  };
  const FIRST_NAME_LABEL = "First Name";
  const LAST_NAME_LABEL = "Last Name";
  const PHONE_LABEL = "Cell Phone";
  const EMAIL_LABEL = "Email";
const TITLE_TEXT = "Invite Participant";
const phoneRegExp = /^\+?[1-9]\d{1,14}$/i;
const emailValidation = yup.string().nullable().email("Email is not valid");
export const phoneValidation = yup
  .string().nullable()
  .matches(phoneRegExp, "Phone number is not valid")
  .test("is-possible-phone-number", "Phone number is not valid", (val) => {
    return !!val ? isPossiblePhoneNumber(val) : true;
  });
export const waitingRoomResendMeetingLinkSchema = yup
  .object()
  .shape({
    participantType: yup.string().required("Participant Type is required"),
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),
    email: emailValidation,
    phone: phoneValidation,
  })
  .test(
    "at-least-one-field",
    "Either a valid Cell Phone or Email is required",
   function (values) { const { phone, email } = values; const isValidPhone = !!phone && phoneRegExp.test(phone) && isPossiblePhoneNumber(phone); const isValidEmail = !!email && yup.string().email().isValidSync(email); if (!isValidPhone && !isValidEmail) { return this.createError({ path: "phone", message: "Either a valid Cell Phone or Email is required", }); } return true; }
  );
export const InviteParticipantModal = ({
  open,
  onClose,
  meetingContext,
  setInfoMessage,
  deviceStatusData,
  getDeviceStatusData
}) => {
const patientData = useMemo(() => ({
  firstName: deviceStatusData?.patientFirstName?.trim() || "",
  lastName: deviceStatusData?.patientLastName?.trim() || "",
  phone: deviceStatusData?.patientPhoneNumber ?? "",
  email: deviceStatusData?.patientEmailAddress ?? "",
  relationshipName: "self",
}), [
  deviceStatusData?.patientFirstName,
  deviceStatusData?.patientLastName,
  deviceStatusData?.patientPhoneNumber,
  deviceStatusData?.patientEmailAddress
]);

const participantsList = useMemo(() => {
  const patient = { ...patientData, participantType: "patient", fullName : "Invite Patient" };

  const emptyInvite = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    relationshipName: "",
    participantType: "inviteNewParticipant",
    fullName : "Invite New Participant"
  };

  const existingInvites = deviceStatusData?.invitedParticipantsList || [];

  return [patient, emptyInvite, ...existingInvites];
}, [deviceStatusData, patientData]);

    const resendInvite = async (data) => {
   
   const result= await postService("/api/virtual-health/meeting/public/invite-to-session", {
      chatId: meetingContext?.chat?.id,
      uriCode: deviceStatusData?.uriCode,
      meetingType:"VISIT",
      participants:[{"firstName": data?.firstName,"lastName":data?.lastName,"email":data?.email,"phone":data?.phone,"relationshipName":data?.relationshipName ?? ""}],
     });
    onClose();
    if(result?.isSuccess){
    setInfoMessage({
      message: "Invite participant outreach sent",
      color: "green",
    });
    setTimeout(() => getDeviceStatusData(), 1000);
    } else {
      setInfoMessage({
        message: "There was an error inviting the participant",
        color: "red",
      });
    }
    setTimeout(() => setInfoMessage(""), 5000);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth  PaperProps={{
        sx: {
          margin: "2px",
          width:"100%",
          height:"100%",
          maxHeight:"100%"
        },
      }}>
      <DialogTitle sx={{fontSize:"16px"}}>{TITLE_TEXT}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <Divider sx={{ mx: 3 }} />
      <DialogContent sx={{ pt: "4px" }}>
      <Box>
      <Formik
        initialValues={{ participantType: "Invite Patient",...patientData }}
        onSubmit={async (data, { setSubmitting  }) => {
          await resendInvite({
            ...data,
          });
          setSubmitting(false);
        }}
        validationSchema={waitingRoomResendMeetingLinkSchema}
        validateOnMount
        validateOnChange
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          isValid,
          isSubmitting,
          handleChange,
        }) => {
          const handleParticipantTypeChange = (e) =>{
            const participant = participantsList?.find((p)=> p?.fullName == e.target.value);
            setFieldValue("participantType",  e.target.value);
            if (participant?.participantType === "inviteNewParticipant" || !participant) {
              setFieldValue("firstName", "");
              setFieldValue("lastName", "");
              setFieldValue("phone", "");
              setFieldValue("email", "");
              setFieldValue("relationshipName", "");
            } else {
              setFieldValue("firstName", participant?.firstName, true);
              setFieldValue("lastName", participant?.lastName, true);
              setFieldValue("phone", participant?.phone, true);
              setFieldValue("email", participant?.email, true);
              setFieldValue("relationshipName", participant?.relationship, true);
            }
          }
          return (
            <Form>
              <Stack spacing={0.5} sx={{ px: 1 }}>
                <StyledLabel>Select Participant</StyledLabel>
                  <FormControl fullWidth>
                    <Select
                      name="participantType"
                      value={values.participantType}
                      size="small"
                      onChange={handleParticipantTypeChange}
                    >
                      {participantsList?.map((participant)=><MenuItem key={participant?.fullName} value={participant?.fullName}>{participant?.fullName}</MenuItem>)}
                    </Select>
              </FormControl>
                  <Typography sx={{py:"4px", fontSize:"14px"}} dangerouslySetInnerHTML={BODY_TEXT_HTML} />
              <Stack
                  sx={{
                    width: "100%",
                    p: 0
                  }}
                >
                  <StyledLabel>
                    {FIRST_NAME_LABEL} <RequiredAsterisk />
                  </StyledLabel>
                  <TextField
                    name="firstName"
                    value={values?.firstName}
                    error={!!errors.firstName && touched.firstName}
                    helperText={touched.firstName && errors.firstName}
                    onBlur={() => setFieldTouched("firstName", true)}
                    fullWidth
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.5)",
                        },
                      },
                      "& .MuiInputBase-input": {
                        height: "35px",
                        padding: " 1px 14px",
                      },
                    }}
                  />
                </Stack>
                  <Stack
                  sx={{
                    width: "100%",
                    p: 0
                  }}
                >
                  <StyledLabel>
                    {LAST_NAME_LABEL} <RequiredAsterisk />
                  </StyledLabel>
                  <TextField
                    name="lastName"
                    value={values?.lastName}
                    error={!!errors.lastName && touched.lastName}
                    helperText={touched.lastName && errors.lastName}
                    onBlur={() => setFieldTouched("lastName", true)}
                    fullWidth
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.5)",
                        },
                      },
                      "& .MuiInputBase-input": {
                        height: "35px",
                        padding: " 1px 14px",
                      },
                    }}
                  />
                </Stack>
                <Stack
                  sx={{
                    width: "100%",
                  }}
                >
                  <StyledLabel>{PHONE_LABEL}</StyledLabel>
                  <InputForPhone
                    name="phone"
                    value={values?.phone}
                    error={!!errors?.phone && touched.phone}
                    className="h-1"
                    setValue={(value) => {
                      setFieldValue("phone", value);
                    }}
                    setTouched={() => setFieldTouched("phone", true)}
                    shadow={false}
                  />
                  {touched.phone && errors.phone && ( <Typography color="error" fontSize="12px"> {errors.phone} </Typography> )}
                </Stack>

                <Stack
                  sx={{
                    width: "100%",
                  }}
                >
                  <StyledLabel>{EMAIL_LABEL}</StyledLabel>
                  <TextField
                    name="email"
                    value={values?.email}
                    error={!!errors.email && touched.email} 
                    helperText={touched.email && errors.email}
                    onBlur={() => setFieldTouched("email", true)}
                    fullWidth
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.5)",
                        },
                      },
                      "& .MuiInputBase-input": {
                        height: "35px",
                        padding: " 1px 14px",
                      },
                    }}
                  />
                </Stack>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "flex-end",
                  mt: "1rem",
                }}
              >
                <Button
                  variant="contained"
                  sx={{color:"#ffffff" ,bgcolor:"#74797B"}}
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{color:"#ffffff",bgcolor:"#3BB8C1"}}
                  type="submit"
                  loading={isSubmitting}
                  disabled={!isValid  || isSubmitting}
                >
                  Send Invite
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Box>
      </DialogContent>
    </Dialog>
  );
};
